import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { PORTIN_ACTION_LABEL, getReportDef, portOutDecisionSchema } from '@app/shared';
import type { PortInAction, PortInDetailDto, ReportRow } from '@app/shared';
import { useNotify } from '../../components/NotifyProvider';
import { ErrorState, LoadingState } from '../../components/PageState';
import { FormDialog } from '../../components/dialogs';
import { TextInput, applyServerErrors } from '../../components/form';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { ReportScreen } from '../reports/ReportPage';

const text = (row: ReportRow, key: string): string => String(row[key] ?? '');

/* --------------------------------- Port in --------------------------------- */

/**
 * "Manage portin": what the request is, the carrier's last answer, the notes, and the
 * actions its state allows (check status, resubmit, cancel, complete, complete swap).
 */
function ManagePortinDialog({ id, onClose }: { id: string | null; onClose: () => void }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const detail = useQuery({
    queryKey: ['ports', 'in', id],
    queryFn: () => api.get<PortInDetailDto>(`/ports/in/${id}`),
    enabled: Boolean(id),
  });
  useEffect(() => setNote(''), [id]);
  const refresh = (next: PortInDetailDto) => {
    queryClient.setQueryData(['ports', 'in', id], next);
    return queryClient.invalidateQueries({ queryKey: ['reports'] });
  };
  const act = useMutation({
    mutationFn: (action: PortInAction) =>
      api.post<PortInDetailDto>(`/ports/in/${id}/actions/${action}`),
    onSuccess: refresh,
  });
  const addNote = useMutation({
    mutationFn: (value: string) =>
      api.post<PortInDetailDto>(`/ports/in/${id}/notes`, { text: value }),
    onSuccess: (next) => {
      setNote('');
      return refresh(next);
    },
  });
  const p = detail.data;
  const busy = act.isPending || addNote.isPending;

  const run = (action: PortInAction) =>
    void act
      .mutateAsync(action)
      .then((next) => notify.success(`${PORTIN_ACTION_LABEL[action]}: ${next.status}.`))
      .catch((err: unknown) => notify.error(err));

  const facts: Array<[string, string]> = p
    ? [
        ['Status', p.manual ? `${p.status} (manual queue)` : p.status],
        ['MDN', p.mdn],
        ['New SIM', p.newSim],
        ['Old MDN', p.oldMdn],
        ['Old SIM', p.oldSim],
        ['Port-in Type', p.portinType],
        ['Swap type', p.swapDone ? `${p.swapType} (done)` : p.swapType],
        ['Old carrier', p.oldCarrier],
        ['Account number', p.accountNumber],
        ['Bill name', p.billing.name],
        ['Bill address', p.billing.address],
        ['Attempt count', String(p.attemptCount)],
        ['Carrier response', p.carrierResponse],
        ['Request date time', formatDateTime(p.requestedAt)],
      ]
    : [];

  return (
    <Dialog open={Boolean(id)} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage portin</DialogTitle>
      <DialogContent dividers>
        {detail.error ? (
          <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
        ) : !p ? (
          <LoadingState />
        ) : (
          <>
            <Box
              component="dl"
              aria-label="Portin details"
              sx={{ display: 'grid', gridTemplateColumns: '170px 1fr', rowGap: 0.5, m: 0, mb: 2 }}
            >
              {facts.map(([label, value]) => (
                <Box key={label} sx={{ display: 'contents' }}>
                  <Typography component="dt" sx={{ fontWeight: 600 }}>
                    {label}
                  </Typography>
                  <Typography component="dd" sx={{ m: 0 }}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Typography variant="h3" sx={{ mb: 0.5 }}>
              Notes
            </Typography>
            {p.notes.length === 0 ? (
              <Typography sx={{ mb: 1 }}>No notes.</Typography>
            ) : (
              <Box component="ul" aria-label="Notes" sx={{ m: 0, mb: 1, pl: 2.5 }}>
                {p.notes.map((n) => (
                  <li key={n.at + n.text}>
                    {n.text} ({n.by}, {formatDateTime(n.at)})
                  </li>
                ))}
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                label="Add notes"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                slotProps={{ htmlInput: { maxLength: 500 } }}
                sx={{ flexGrow: 1 }}
              />
              <Button
                variant="outlined"
                disabled={busy || note.trim() === ''}
                onClick={() =>
                  void addNote.mutateAsync(note.trim()).catch((err: unknown) => notify.error(err))
                }
              >
                Add
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {(p?.actions ?? []).map((action) => (
          <Button
            key={action}
            variant="contained"
            color={action === 'cancel' ? 'error' : 'primary'}
            disabled={busy}
            onClick={() => run(action)}
          >
            {PORTIN_ACTION_LABEL[action]}
          </Button>
        ))}
        <Button onClick={onClose} disabled={busy}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * One of the port-in lists (Pending, Manual queue, Failed, Cancel, Completed, Pending swaps).
 * The lists differ in their filters and columns, which come from the screen definition.
 */
export function PortInListPage({ screen }: { screen: string }) {
  const [managing, setManaging] = useState<string | null>(null);
  return (
    <>
      <ReportScreen
        def={getReportDef(screen)!}
        rowActions={(row) => (
          <Button
            size="small"
            aria-label={`Manage portin ${text(row, 'mdn')}`}
            onClick={() => setManaging(text(row, 'id'))}
          >
            Manage
          </Button>
        )}
      />
      <ManagePortinDialog id={managing} onClose={() => setManaging(null)} />
    </>
  );
}

/** Portin History (PC778): every message exchanged about a port-in. */
export function PortinHistoryPage() {
  return <ReportScreen def={getReportDef('portin-history')!} />;
}

/* --------------------------------- Port out -------------------------------- */

interface DecisionForm {
  decision: 'Completed' | 'Rejected';
  reason: string;
}

/** "Manage Portout": let the number go, or refuse the request with a reason. */
function ManagePortoutDialog({
  request,
  onClose,
}: {
  request: ReportRow | null;
  onClose: () => void;
}) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { control, handleSubmit, reset, setError } = useForm<DecisionForm>({
    defaultValues: { decision: 'Completed', reason: '' },
    resolver: zodResolver(portOutDecisionSchema),
  });
  const decision = useWatch({ control, name: 'decision' });
  useEffect(() => {
    if (request) reset({ decision: 'Completed', reason: '' });
  }, [request, reset]);
  const decide = useMutation({
    mutationFn: (input: DecisionForm) =>
      api.post<{ status: string }>(`/ports/out/${text(request!, 'id')}/decision`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reports'] }),
  });
  const submit = handleSubmit(async (values) => {
    try {
      const done = await decide.mutateAsync(values);
      notify.success(`Port-out ${done.status.toLowerCase()}.`);
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });
  return (
    <FormDialog
      open={Boolean(request)}
      title="Manage Portout"
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={decide.isPending}
      maxWidth="xs"
    >
      <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
        {request ? (
          <Typography>
            {text(request, 'firstName')} {text(request, 'lastName')} ({text(request, 'customerId')}
            ), MDN {text(request, 'mdn')}
          </Typography>
        ) : null}
        <Controller
          control={control}
          name="decision"
          render={({ field }) => (
            <FormControl>
              <FormLabel id="portout-decision-label">Decision</FormLabel>
              <RadioGroup
                aria-labelledby="portout-decision-label"
                name={field.name}
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
              >
                <FormControlLabel
                  value="Completed"
                  label="Complete the port-out"
                  control={<Radio size="small" />}
                />
                <FormControlLabel
                  value="Rejected"
                  label="Reject the request"
                  control={<Radio size="small" />}
                />
              </RadioGroup>
            </FormControl>
          )}
        />
        {decision === 'Completed' ? (
          <Alert severity="warning">The number leaves and the account becomes Inactive.</Alert>
        ) : (
          <TextInput
            control={control}
            name="reason"
            label="Reason"
            required
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: 250 } }}
          />
        )}
      </Box>
    </FormDialog>
  );
}

/** One of the five port-out lists. Pending requests are answered from "Manage Portout". */
export function PortOutListPage({ screen }: { screen: string }) {
  const [managing, setManaging] = useState<ReportRow | null>(null);
  const pending = screen.endsWith('-pending');
  return (
    <>
      <ReportScreen
        def={getReportDef(screen)!}
        rowActions={
          pending
            ? (row) => (
                <Button
                  size="small"
                  aria-label={`Manage Portout ${text(row, 'mdn')}`}
                  onClick={() => setManaging(row)}
                >
                  Manage Portout
                </Button>
              )
            : undefined
        }
      />
      <ManagePortoutDialog request={managing} onClose={() => setManaging(null)} />
    </>
  );
}
